import React, { useEffect, useState, useCallback } from 'react';
import { ShieldCheck, CheckCircle2, AlertCircle, Loader2, Ban } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CONSENT_VERSION = 'v1-2026-02';

/**
 * PilotOptInCard — Fase 3.6 read-only pilot opt-in/out (klant zelfservice).
 *
 * Strikt read-only. Geen actuators. Geen live sturing. Synthetic heartbeats
 * worden duidelijk gelabeld. Opt-in/out wordt geaudit.
 *
 * Props:
 *   - sessionId: deterministische session-id (zoals afgeleid in Account).
 */
export default function PilotOptInCard({ sessionId }) {
  const sid = sessionId || (typeof window !== 'undefined'
    ? (window.localStorage?.getItem('ps_session_id') || '')
    : '');

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [status, setStatus] = useState({ opted_in: false, state: 'none' });

  const logEvent = useCallback(async (event) => {
    try {
      await fetch(`${API}/track/funnel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event, session_id: sid || 'pilot-card' }),
      });
    } catch { /* swallow */ }
  }, [sid]);

  const refresh = useCallback(async () => {
    if (!sid || sid.length < 4) { setLoading(false); return; }
    try {
      const r = await fetch(`${API}/devices/pilot/status?session_id=${encodeURIComponent(sid)}`);
      const j = await r.json();
      if (r.ok && j.ok) setStatus(j);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [sid]);

  useEffect(() => { refresh(); }, [refresh]);

  const optIn = useCallback(async () => {
    if (!sid || sid.length < 8) { setErr('Sessie is te kort om opt-in vast te leggen.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/devices/pilot/opt-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid, consent_version: CONSENT_VERSION, channel: 'app' }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.detail || 'opt_in_failed');
      setStatus({ opted_in: true, state: 'opted_in', consent_version: CONSENT_VERSION });
      logEvent('pilot_opt_in');
    } catch (e) {
      setErr(e.message || 'opt_in_failed');
    } finally { setBusy(false); }
  }, [sid, logEvent]);

  const optOut = useCallback(async () => {
    if (!sid || sid.length < 8) { setErr('Sessie is te kort.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/devices/pilot/opt-out`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid, reason: 'customer self-service' }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.detail || 'opt_out_failed');
      setStatus({ opted_in: false, state: 'opted_out' });
      logEvent('pilot_opt_out');
    } catch (e) {
      setErr(e.message || 'opt_out_failed');
    } finally { setBusy(false); }
  }, [sid, logEvent]);

  return (
    <section
      className="rounded-2xl border border-white/10 p-5 space-y-3"
      style={{ background: 'linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
      data-testid="mp-pilot-opt-in-card"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 flex-shrink-0">
          <ShieldCheck className="w-4 h-4" />
        </span>
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
            Doe mee met de read-only meetpilot
          </h3>
          <p className="text-[12.5px] text-white/65 leading-snug mt-0.5">
            U helpt Powersure verbeteren. Wij <span className="text-emerald-200">meten alleen</span> —
            <span className="text-emerald-200"> niets wordt automatisch geschakeld</span>.
            U kunt op elk moment stoppen.
          </p>
        </div>
      </div>

      <ul className="text-[12px] text-white/75 space-y-1 pl-1">
        <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 mt-0.5 shrink-0" /> Read-only — geen sturing, geen boiler/EV/warmtepomp/batterij aansturing.</li>
        <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 mt-0.5 shrink-0" /> Synthetische status-pings zijn altijd gelabeld als <span className="font-mono text-emerald-200">synthetic</span>.</li>
        <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 mt-0.5 shrink-0" /> Opt-out wist uw pilot-toestemming via &ldquo;Mijn device-data&rdquo;.</li>
        <li className="flex items-start gap-2"><Ban className="w-3.5 h-3.5 text-rose-300 mt-0.5 shrink-0" /> Geen actuators. Geen automatische sturing. Geen externe deling.</li>
      </ul>

      {loading ? (
        <div className="text-sm text-white/55 inline-flex items-center gap-2" data-testid="mp-pilot-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Status laden…
        </div>
      ) : status.opted_in ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div
            className="inline-flex items-center gap-2 text-sm text-emerald-100"
            data-testid="mp-pilot-status-opted-in"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-300" />
            <span>U bent aangemeld voor de meetpilot (versie {status.consent_version || CONSENT_VERSION}).</span>
          </div>
          <button
            type="button"
            onClick={optOut}
            disabled={busy}
            data-testid="mp-pilot-opt-out-btn"
            className="px-3 py-1.5 rounded-md border border-white/15 bg-white/5 hover:bg-white/10 text-white/85 text-xs disabled:opacity-60"
          >
            {busy ? 'Bezig…' : 'Stop met de pilot'}
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="inline-flex items-center gap-2 text-sm text-white/65" data-testid="mp-pilot-status-not-opted-in">
            <AlertCircle className="w-4 h-4 text-amber-300" />
            <span>U doet nog niet mee. Aanmelden kan met één klik.</span>
          </div>
          <button
            type="button"
            onClick={optIn}
            disabled={busy}
            data-testid="mp-pilot-opt-in-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-sm font-semibold disabled:opacity-60"
          >
            {busy ? 'Bezig…' : 'Doe mee (read-only)'}
          </button>
        </div>
      )}

      {err && (
        <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-3 text-sm text-amber-100" data-testid="mp-pilot-err">
          {err}
        </div>
      )}

      <p className="text-[11px] text-white/45 leading-relaxed pt-1">
        Toestemmingsversie <span className="font-mono">{CONSENT_VERSION}</span>. Uw opt-in/opt-out
        wordt opgenomen in uw device-data export.
      </p>
    </section>
  );
}
